import { Select, Spin } from 'antd';
import React, { useMemo, useState } from 'react';
import { useGetDocumentProcedureValuesQuery } from '@client/features/Datasets/services/api';
import type { DocumentFilterCondition } from '../../types';

interface ProcedureIcdCodesSelectProps {
    condition: DocumentFilterCondition;
    groupId: string;
    updateCondition: (groupId: string, conditionId: string, updates: Partial<DocumentFilterCondition>) => void;
}

/**
 * Multi-select for procedure ICD codes
 */
export const ProcedureIcdCodesSelect: React.FC<ProcedureIcdCodesSelectProps> = ({
    condition,
    groupId,
    updateCondition,
}) => {
    const [procedureSearch, setProcedureSearch] = useState('');

    const { data: procedureData, isLoading: isProcedureLoading } = useGetDocumentProcedureValuesQuery({
        search: procedureSearch,
        limit: 100,
    });

    const procedureOptions = useMemo(
        () =>
            procedureData?.values
                ?.filter((v: any) => v.value) // Filter out items without value
                .map((v: any) => ({
                    label: v.icd_code
                        ? `${v.label} (${v.icd_code})${v.count ? ` [${v.count} docs]` : ''}`
                        : `${v.label}${v.count ? ` [${v.count} docs]` : ''}`,
                    value: v.value, // Use value from backend (ICD code if available, otherwise concept_name)
                    title: v.label, // Store label for valueTitleMap
                })) || [],
        [procedureData]
    );

    const handleChange = (vals: string[]) => {
        if (!vals || vals.length === 0) {
            updateCondition(groupId, condition.id, { value: undefined, valueTitleMap: undefined });
            return;
        }

        // Build valueTitleMap from selected values
        const valueTitleMap: Record<string, string> = {};
        vals.forEach(val => {
            const option = procedureOptions.find(opt => opt.value === val);
            if (option && option.title) {
                valueTitleMap[String(val)] = option.title;
            }
        });

        updateCondition(groupId, condition.id, {
            value: vals,
            valueTitleMap: Object.keys(valueTitleMap).length > 0 ? valueTitleMap : undefined,
        });
    };

    return (
        <Select
            mode="multiple"
            placeholder="Search procedures..."
            className="w-full [&_.ant-select-selector]:!min-h-[32px]"
            allowClear
            showSearch
            filterOption={false}
            onSearch={setProcedureSearch}
            options={procedureOptions}
            loading={isProcedureLoading}
            value={condition.value}
            onChange={handleChange}
            notFoundContent={isProcedureLoading ? <Spin size="small" /> : 'No procedures found'}
        />
    );
};
